/* ───────────────────────────── WQ Analyte Controls Component ─────────────────────── */

import { useState, useEffect } from "react";
import GoButton from '../widgetsAndUtils/GoButton';
import ToggleSwitch from '../widgetsAndUtils/ToggleSwitch';
import { useAppStateContext } from '../../hooks/useAppState';
import EMCOverTime from "../analysisPanels/wq/EMCOverTime";

const API_BASE = process.env.API_BASE ?? "";

// Default thresholds for specific analytes using pattern matching
// Arbitrarily determined by Elizabeth based on median concentrations, etc (Or some other stuff that she does that I don't really know about)
const DEFAULT_THRESHOLD_RULES = [
    { pattern: /copper/i, threshold: 15 },      // ug/L
    { pattern: /nitrogen/i, threshold: 2 },     // mg/L
    { pattern: /phosphorus/i, threshold: 0.2 }, // mg/L
    { pattern: /solids/i, threshold: 10 },        // mg/L
    { pattern: /zinc/i, threshold: 15 }        // ug/L
];

// Label mappings for display
const ANALYSIS_TYPE_LABELS = {
  'bmptype': 'BMP Type',
  'individual': 'Individual BMP',
  'treatmenttrain': 'Treatment Train'
};

// Helper function to get display label
const getDisplayLabel = (value, labelMap) => {
  return labelMap[value] || value;
};

function getDefaultThreshold(analytename) {
  for (const rule of DEFAULT_THRESHOLD_RULES) {
    if (rule.pattern.test(analytename)) {
      return rule.threshold;
    }
  }
  return 0.5; // fallback default
}

// This component handles only the analyte selection and thresholds for water quality data
export default function WQAnalyteControls({ onResults }) {
  const { bmpState, state, updateWqState, updateWQIndexData, updateEMCOverTimeData, updateMpiData } = useAppStateContext();

  const [analytes,  setAnalytes]    = useState([]);
  const [error,     setError]       = useState(null);

  // Extract values from context
  const { selectedAnalytes } = state.wq;
  const { analysistype, bmptype, sitename, bmpname } = bmpState;

  // Helper function to get BMP type display name (if we had access to bmptypes data)
  const getBmpTypeDisplayName = (bmptypeValue) => {
    // For now, we'll use a simple mapping since we don't have access to the full bmptypes data here
    const BMP_TYPE_LABELS = {
      'BR': 'Bioretention',
      'GS': 'Grass Strip',
      // Add other mappings as needed
    };
    return BMP_TYPE_LABELS[bmptypeValue] || bmptypeValue;
  };

 

  const handleThresholdChange = (analytename, threshold) => {
    const updatedAnalytes = selectedAnalytes.map(analyte => 
      analyte.analytename === analytename 
        ? { ...analyte, threshold: parseFloat(threshold) || 0 }
        : analyte
    );
    updateWqState({ selectedAnalytes: updatedAnalytes });
  };

  const removeAnalyte = (analytename) => {
    const updatedAnalytes = selectedAnalytes.filter(analyte => analyte.analytename !== analytename);
    updateWqState({ selectedAnalytes: updatedAnalytes });
  };

  // Helper function to handle API responses
  const handleApiResponse = async (res) => {
    const data = await res.json();
    
    if (!res.ok) {
      // Handle 422 errors specifically with user-friendly messages
      if (res.status === 422) {
        throw new Error(data.error || "No data found for the selected parameters. Please check your selections and try again.");
      }
      
      throw new Error(`HTTP ${res.status}: ${data.error || data.message || 'Unknown error'}`);
    }
    
    return data;
  };

  // Fetch analytenames for WQ data
  useEffect(() => {
    if (!analysistype) {
      setAnalytes([]);
      return;
    }

    setAnalytes([]);
    updateWqState({ selectedAnalytes: [] }); // Reset selected analytes when changing analysis type
    setError(null);

    let body = {};
    let endpoint = "";

    if (analysistype === "individual" && sitename && bmpname) {
      endpoint = `${API_BASE}/selectioncontrols/wq/${analysistype}/analytenames`;
      body = { sitename, bmpname };
    } else if (analysistype === "bmptype" && bmptype) {
      endpoint = `${API_BASE}/selectioncontrols/wq/${analysistype}/analytenames`;
      body = { bmptype };
    } else if (analysistype === "treatmenttrain" && sitename) {
      endpoint = `${API_BASE}/selectioncontrols/wq/${analysistype}/analytenames`;
      body = { sitename };
    }

    if (endpoint) {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
        .then(handleApiResponse)
        .then(data => {
          setAnalytes(data.analytenames);
        })
        .catch(e => {
          setError(e.message);
        });
    }
  }, [bmpname, bmptype, sitename, analysistype]);

  // --- Render component ---
  const [showAnalyteDropdown, setShowAnalyteDropdown] = useState(false);
  const availableAnalytes = analytes.filter(a => !selectedAnalytes.some(sa => sa.analytename === a));

  return (
    <div className="flex justify-center items-start w-full mt-8">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow p-0 border border-gray-200">
        {/* BMP Selection Status Section */}
        <div className="border-b px-4 sm:px-6 py-4">
          <h3 className="text-md font-semibold mb-2">Current BMP Selection:</h3>
          <div className="text-sm text-gray-600">
            <p><strong>Analysis Type:</strong> {getDisplayLabel(analysistype, ANALYSIS_TYPE_LABELS) || 'Not selected'}</p>
            {analysistype === "individual" && (
              <>
                <p><strong>Site Name:</strong> {sitename || 'Not selected'}</p>
                <p><strong>BMP Name:</strong> {bmpname || 'Not selected'}</p>
              </>
            )}
            {analysistype === "bmptype" && (
              <p><strong>BMP Type:</strong> {getBmpTypeDisplayName(bmptype) || 'Not selected'}</p>
            )}
            {analysistype === "treatmenttrain" && (
              <p><strong>Site Name:</strong> {sitename || 'Not selected'}</p>
            )}
          </div>
        </div>

        {/* Analyte & Thresholds Section */}
        {analysistype && (
          <div className="border-b px-4 sm:px-6 py-4">
            <h3 className="text-md font-semibold mb-2">Analytes & Thresholds:</h3>
            {/* Analyte List */}
            <div className="flex flex-col gap-2 mb-2">
              {selectedAnalytes && selectedAnalytes.length > 0 ? (
                selectedAnalytes.map((analyte, index) => (
                  <div key={analyte.analytename} className="flex flex-col sm:flex-row sm:items-center border rounded px-3 py-2 bg-white gap-2 sm:gap-0">
                    <div className="flex-1 text-sm break-words pr-2">{analyte.analytename}</div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <label className="text-xs text-gray-600 whitespace-nowrap">Threshold:</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={analyte.threshold}
                        onChange={(e) => handleThresholdChange(analyte.analytename, e.target.value)}
                        className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => removeAnalyte(analyte.analytename)}
                        className="text-red-500 hover:text-red-700 text-lg px-2 py-1 rounded hover:bg-red-50 min-h-[44px] flex items-center justify-center"
                        title="Remove analyte"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-400 italic">No analytes selected.</div>
              )}
            </div>
            {/* Add Analyte Button and Dropdown */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
              <button
                className="text-blue-600 hover:underline text-sm font-medium px-2 py-1 rounded self-start"
                onClick={() => setShowAnalyteDropdown(v => !v)}
                type="button"
              >
                [Add Analyte +]
              </button>
              {showAnalyteDropdown && availableAnalytes.length > 0 && (
                <div className="relative w-full sm:w-auto sm:min-w-[250px] sm:max-w-[400px]">
                  <select
                    className="w-full border rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                    defaultValue=""
                    onChange={e => {
                      const analytename = e.target.value;
                      if (analytename) {
                        const defaultThresh = getDefaultThreshold(analytename);
                        updateWqState({ 
                          selectedAnalytes: [
                            ...selectedAnalytes, 
                            { analytename, threshold: defaultThresh }
                          ] 
                        });
                        setShowAnalyteDropdown(false);
                      }
                    }}
                  >
                    <option value="" disabled>Select analyte…</option>
                    {availableAnalytes.map(a => (
                      <option key={a} value={a} className="py-1">
                        {a.length > 50 ? `${a.substring(0, 47)}...` : a}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showAnalyteDropdown && availableAnalytes.length === 0 && (
                <span className="text-xs text-gray-400">
                    {
                        bmptype || (sitename && bmpname)
                        ? 'No more analytes available'
                        : 'Please select a BMP or BMP Type'
                    }
                </span>
              )}
            </div>
          </div>
        )}

        {/* Go Button Row */}
        <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-4 px-4 sm:px-6 py-4">
        {analysistype === 'individual' && (
          <ToggleSwitch
            id="comparison-mode-toggle"
            checked={!!state.wq.comparisonMode}
            onChange={e => updateWqState({ comparisonMode: e.target.checked })}
            label="Show Comparison Data"
          />
        )}
          <GoButton
            datatype="wq"
            apiResource="analysisdata"
            onSuccess={(data) => {
              console.log('wq data',data)

              updateWqState({ indexData: {}, emcOverTimeData: [] });
              updateWQIndexData(data.performance_index_data);
              updateEMCOverTimeData(data.emc_over_time);
              // Build new MPI data array for WQ analytes
              const newMpiData = state.wq.selectedAnalytes
                .filter(analyteConfig => data.performance_index_data[analyteConfig.analytename] && data.performance_index_data[analyteConfig.analytename].index_summary && data.performance_index_data[analyteConfig.analytename].index_summary.length > 0)
                .map(analyteConfig => {
                  // Find the selected BMP data (not comparison data)
                  const selectedBmpData = data.performance_index_data[analyteConfig.analytename].index_summary.find(d => 
                    typeof d.bmp === 'string' && !d.bmp.startsWith('Other')
                  );
                  
                  return {
                    analytename: analyteConfig.analytename,
                    priority: analyteConfig.priority ?? 1,
                    score: selectedBmpData?.performance_index || data.performance_index_data[analyteConfig.analytename].index_summary[0].performance_index,
                    weight: NaN,
                    threshold: analyteConfig.threshold
                  };
                });
              // Preserve Hydrology if present
              const hydrologyObj = state.mpiData.find(a => a.analytename === "Hydrology");
              if (hydrologyObj) newMpiData.push(hydrologyObj);
              updateMpiData(newMpiData);
              if (onResults && data && data.performance_index_data) onResults(data.performance_index_data);
            }}
            onError={(error) => {
              setError(error.message);
              updateWqState({ indexData: {}, emcOverTimeData: [] });
            }}
            disabled={!analysistype || (analysistype === "individual" && (!sitename || !bmpname)) || 
                     (analysistype === "bmptype" && !bmptype) || 
                     (analysistype === "treatmenttrain" && !sitename) ||
                     (!selectedAnalytes || selectedAnalytes.length === 0)}
          />
        </div>

        {error && (
          <div className="px-4 sm:px-6 pb-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
              <strong>Error:</strong> {error}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 